import React, { useState, useEffect } from "react";
import { X, GripVertical, Save } from "lucide-react";

export default function ColumnReorderModal({
  isOpen,
  onClose,
  columns,
  onSubmit,
}) {
  const [orderedColumns, setOrderedColumns] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && columns) {
      // 시스템 컬럼 제외하고 정렬
      const userColumns = columns
        .filter(
          (col) => !["id", "created_at", "updated_at"].includes(col.column_key)
        )
        .sort((a, b) => a.column_order - b.column_order);
      setOrderedColumns(userColumns);
    }
  }, [isOpen, columns]);

  if (!isOpen) return null;

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedItem === null) return;

    const draggedColumn = orderedColumns[draggedItem];
    const newColumns = [...orderedColumns];

    // Remove dragged item
    newColumns.splice(draggedItem, 1);

    // Insert at new position
    newColumns.splice(dropIndex, 0, draggedColumn);

    setOrderedColumns(newColumns);
    setDraggedItem(null);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Create column_orders object with new order
      const columnOrders = {};
      orderedColumns.forEach((col, index) => {
        columnOrders[col.id] = index + 1; // 1-based indexing
      });

      await onSubmit(columnOrders);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOrderedColumns([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">컬럼 순서 변경</h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            드래그하여 컬럼 순서를 변경하세요
          </p>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {orderedColumns.map((column, index) => (
              <div
                key={column.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-move hover:bg-gray-100 transition-colors ${
                  draggedItem === index ? "opacity-50" : ""
                }`}
              >
                <GripVertical className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {column.display_name}
                  </p>
                  <p className="text-xs text-gray-500">{column.data_type}</p>
                </div>
                <span className="text-sm text-gray-400">#{index + 1}</span>
              </div>
            ))}
          </div>

          {orderedColumns.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>재정렬할 컬럼이 없습니다</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              취소
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={submitting || orderedColumns.length === 0}
            >
              <Save className="w-4 h-4" />
              {submitting ? "저장 중..." : "순서 저장"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
